import { useParams, Link } from 'react-router-dom';
import { SEOHead } from '@/components/seo/SEOHead';
import { Calendar, Clock, ArrowLeft, Tag } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useSanityBlogPost } from '@/lib/sanity.hooks';

export const BlogPostPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const { data: post, loading, error } = useSanityBlogPost(slug || '');

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  if (loading) {
    return (
      <div className="pt-28 min-h-screen flex items-center justify-center">
        <p className="text-white/90">Loading blog post...</p>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="relative pt-28 min-h-screen flex items-center justify-center overflow-hidden">
        <div className="relative z-10 text-center glass-panel rounded-3xl p-8 max-w-md mx-auto border border-slate-800">
          <h1 className="text-2xl font-bold text-white mb-4">Post Not Found</h1>
          <p className="text-white/60 text-sm mb-6">
            The requested article could not be resolved in the database.
          </p>
          <Link to="/blog" className="text-emerald-400 hover:underline font-semibold">
            ← Back to Blog Overview
          </Link>
        </div>
      </div>
    );
  }

  const ogImageUrl = post.featuredImage?.asset?.url || undefined;

  return (
    <>
      <SEOHead
        title={post.title}
        description={post.excerpt}
        canonicalUrl={`/blog/${post.slug?.current}`}
        ogImage={ogImageUrl}
        type="article"
        schema={{
          '@context': 'https://schema.org',
          '@type': 'BlogPosting',
          headline: post.title,
          description: post.excerpt,
          image: ogImageUrl,
          datePublished: post.publishedAt,
          author: {
            '@type': 'Person',
            name: post.author?.name || 'Rooflyzer Team',
          },
        }}
      />

      <article className="relative pt-28 min-h-screen px-4 sm:px-6 pb-20 overflow-hidden">
        <div className="relative z-10 max-w-4xl mx-auto">
          {/* Back Link */}
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-white/60 hover:text-white mb-8 transition-colors font-semibold text-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Blog Newsroom
          </Link>

          {/* Header */}
          <header className="mb-10">
            {post.categories?.[0] && (
              <span className="inline-block px-3 py-1 text-xs font-mono font-medium border border-slate-700 bg-slate-800/80 text-slate-300 rounded-full mb-4">
                {post.categories[0].title}
              </span>
            )}
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-6 leading-tight">
              {post.title}
            </h1>
            {post.excerpt && (
              <p className="text-lg sm:text-xl text-white/70 mb-6 leading-relaxed">
                {post.excerpt}
              </p>
            )}

            <div className="flex items-center gap-6 text-sm text-white/40 border-b border-white/5 pb-6">
              {post.publishedAt && (
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  {formatDate(post.publishedAt)}
                </div>
              )}
              {post.author?.name && (
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  Written by {post.author.name}
                </div>
              )}
            </div>

            {post.tags && post.tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-4">
                {post.tags.map((tag, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center gap-1.5 px-3 py-1 text-xs bg-slate-800/60 border border-slate-700 rounded-full text-slate-300 font-mono"
                  >
                    <Tag className="w-3 h-3 text-emerald-400" />
                    {tag}
                  </span>
                ))}
              </div>
            )}
          </header>

          {/* Featured Image */}
          {post.featuredImage?.asset?.url && (
            <div className="mb-12 rounded-3xl overflow-hidden glass-panel p-1">
              <div className="rounded-[22px] overflow-hidden border border-white/10">
                <img
                  src={post.featuredImage.asset.url}
                  alt={post.featuredImage.alt || post.title}
                  className="w-full h-auto object-cover"
                />
              </div>
            </div>
          )}

          {/* Content Container */}
          <div className="prose prose-invert max-w-none glass-panel rounded-3xl p-8 md:p-12 leading-relaxed">
            {post.body ? (
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{post.body}</ReactMarkdown>
            ) : (
              <p className="text-white/40 italic text-center">
                No content blocks published for this post.
              </p>
            )}
          </div>
        </div>
      </article>
    </>
  );
};

export default BlogPostPage;
